import {
  BuildSearchUrlOptions,
  buildSearchUrl,
} from 'components/www/availability-search/SearchLauncher';
import { ChronoUnit, LocalDateTime } from 'js-joda';
import { PlatterCardProps } from 'omakase/PlatterCard/PlatterCard';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer } from '@tock/coretypes';
import { getAutoFormatUrl } from '@tock/images';

type ExplorePlatterCardType = Omit<PlatterCardProps, 'linkTo'> & {
  linkTo?: string;
  type?: consumer.OfferingSearchType;
};

type Mode = 'default' | 'holidayThanksgiving' | 'holidayRest';

export interface Props {
  metroArea?: consumer.IMetroArea;
  mode?: Mode;
}

export const buildCitySearchUrl = ({ ...rest }: BuildSearchUrlOptions) =>
  buildSearchUrl({
    ...rest,
  });

export const getExplorePlatterCards = ({
  metroArea,
  mode = 'default',
}: Props): PlatterCardProps[] => {
  const explorePlatterCards: ExplorePlatterCardType[] = getExplorePlatterCardsInternal(mode);

  const date = LocalDateTime.now().truncatedTo(ChronoUnit.HOURS).plusHours(1).toLocalDate();

  return explorePlatterCards.map(({ linkTo, type, ...rest }) => {
    const typeAsOfferingSearchType = type as consumer.OfferingSearchType;
    return {
      ...rest,
      metroArea,
      linkTo:
        linkTo ??
        buildCitySearchUrl({
          metroArea,
          date,
          guests: '2',
          type: typeAsOfferingSearchType,
        }),
    };
  });
};

const getExplorePlatterCardsInternal = (mode: Mode): ExplorePlatterCardType[] => {
  switch (mode) {
    case 'default':
      return [
        {
          title: 'Dine in',
          itemId: 1,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-dinein.jpg'
          ),
          imgAltLabel: 'Multiple dishes served with wine',
          type: 'DINE_IN_EXPERIENCES',
        },
        {
          title: 'Pickup',
          itemId: 2,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-pickup.jpg'
          ),
          imgAltLabel: 'Several bagged orders of food to-go',
          type: 'ORDER_PICKUP',
        },
        {
          title: 'Delivery',
          itemId: 3,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-delivery.jpg'
          ),
          imgAltLabel: 'Opened container revealing a hamburger',
          type: 'ORDER_DELIVERY',
        },
        {
          title: 'Events',
          itemId: 4,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-events.jpg'
          ),
          imgAltLabel: 'Chef in a kitchen',
          linkTo: '/c/events',
        },
        {
          title: 'Wineries',
          itemId: 5,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-wineries.jpg'
          ),
          imgAltLabel: 'People strolling a vineyard sipping wine',
          linkTo: '/collections/wineries',
        },
      ];

    case 'holidayThanksgiving':
      return [
        {
          title: 'Thanksgiving',
          itemId: 1,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-thanksgiving.png'
          ),
          imgAltLabel: 'Multiple thanksgiving dishes',
          linkTo: '/collections/thanksgiving',
        },
        {
          title: 'Holidays',
          itemId: 2,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-holidays.png'
          ),
          imgAltLabel: 'Two holiday drinks',
          linkTo: '/collections/holidays',
        },
        {
          title: "New Year's Eve",
          itemId: 3,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-newyearseve.png'
          ),
          imgAltLabel: 'Oysters and wine',
          linkTo: '/collections/nye',
        },
        {
          title: 'Events',
          itemId: 4,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-events_2.png'
          ),
          imgAltLabel: 'Friends having drinks',
          linkTo: '/c/events',
        },
        {
          title: 'Wineries',
          itemId: 5,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-wineries_2.png'
          ),
          imgAltLabel: 'A glass and bottle of red wine',
          linkTo: '/collections/wineries',
        },
      ];
    case 'holidayRest':
      return [
        {
          title: 'Holidays',
          itemId: 2,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-holidays.png'
          ),
          imgAltLabel: 'Two holiday drinks',
          linkTo: '/collections/holidays',
        },
        {
          title: "New Year's Eve",
          itemId: 3,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-newyearseve.png'
          ),
          imgAltLabel: 'Oysters and wine',
          linkTo: '/collections/nye',
        },
        {
          title: 'Events',
          itemId: 4,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-events_2.png'
          ),
          imgAltLabel: 'Friends having drinks',
          linkTo: '/c/events',
        },
        {
          title: 'Bars',
          itemId: 4,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-bars.jpg'
          ),
          imgAltLabel: 'A rustic festive bar',
          linkTo: '/collections/cuisine/bar',
        },
        {
          title: 'Wineries',
          itemId: 5,
          imgSrc: getAutoFormatUrl(
            'https://storage.googleapis.com/tock-public-assets/brand-assets/experience-platter/platter-wineries_2.png'
          ),
          imgAltLabel: 'A glass and bottle of red wine',
          linkTo: '/collections/wineries',
        },
      ];
    default:
      assertNever(mode);
  }
};
